import React, { useState, useRef } from 'react';
import './FileUpload.css'; // Reuse existing styles
import apiClient from '../../services/apiClient';
import { getMediaUrl } from '../../utils/mediaUrl';

const ImageUpload = ({ value, onChange, label = "Photo", placeholder = "Télécharger une image" }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = async (file) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image valide.');
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('L\'image est trop volumineuse. Taille maximale: 5MB');
      return;
    }

    setUploading(true);
    
    try {
      console.log('📡 Uploading image to backend...');
      const response = await apiClient.uploadFiles([file]);
      console.log('📤 Upload response:', response);
        
        // Check if upload was successful first
        if (!response.success || !response.files || response.files.length === 0) {
          throw new Error(response.message || 'Upload failed - no files returned');
        }
        
        // Check for individual file errors
        const fileData = response.files[0];
        if (fileData.error) {
          throw new Error(fileData.error);
        }
        
        // Debug: Log the actual structure of files array
        console.log('📋 First file object structure:', fileData);
        console.log('📋 All file object keys:', Object.keys(fileData));
        
        const fileObj = fileData;
        const imageUrl = getMediaUrl(fileObj);

        if (!imageUrl || imageUrl.includes('undefined') || imageUrl.includes('null')) {
          console.log('🔍 Available fileObj properties:', Object.keys(fileObj));
          console.log('🔍 FileObj values:', fileObj);
          throw new Error('No valid filename found in upload response: ' + JSON.stringify(fileObj));
        }

        onChange(imageUrl);
        console.log('✅ Image uploaded successfully:', imageUrl);
        console.log('📁 Final image URL:', imageUrl);
    } catch (error) {
      console.error('❌ Upload error:', error);
      
      // Show more specific error message
      let errorMessage = 'Erreur lors du téléchargement. ';
      if (error.message && error.message.includes('Authentication required')) {
        errorMessage += 'Session expirée — déconnectez-vous puis reconnectez-vous au CMS.';
      } else if (error.message && error.message.includes('Failed to fetch')) {
        errorMessage += 'Le serveur ne répond pas. Vérifiez votre connexion internet.';
      } else if (error.message && error.message.includes('404')) {
        errorMessage += 'Endpoint non trouvé. Vérifiez votre backend.';
      } else if (error.message && error.message.includes('413')) {
        errorMessage += 'Fichier trop volumineux.';
      } else if (error.message && error.message.includes('400')) {
        errorMessage += 'Format de fichier non supporté.';
      } else {
        errorMessage += `Détails: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    onChange('');
  };

  const previewUrl = getMediaUrl(value);

  return (
    <div className="image-upload-container">
      {value ? (
        <div className="image-preview">
          <div className="preview-header">
            <h4>{label}</h4>
            <button 
              type="button"
              className="btn-remove"
              onClick={handleRemove}
              title="Supprimer l'image"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          
          <div className="preview-content">
            {previewUrl ? (
              <img 
                src={previewUrl} 
                alt={label}
                className="image-preview-img"
                onError={(e) => {
                  console.error('❌ Image failed to load:', previewUrl, 'raw value:', value);
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
                onLoad={() => {
                  console.log('✅ Image loaded successfully:', previewUrl);
                }}
              />
            ) : null}
            <div className="image-placeholder" style={{ display: previewUrl ? 'none' : 'flex' }}>
              <i className="fas fa-image"></i>
              <span>Aperçu non disponible</span>
            </div>
          </div>
          
          {/* Hidden: Image path not shown to client */}
        </div>
      ) : (
        <>
          <div
            className={`file-drop-zone ${isDragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="drop-zone-content">
              {uploading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  <p>Téléchargement en cours...</p>
                </>
              ) : (
                <>
                  <i className="fas fa-cloud-upload-alt"></i>
                  <p>{placeholder}</p>
                  <p className="drop-zone-hint">Glissez-déposez une image ou cliquez pour parcourir</p>
                  <p className="file-requirements">
                    <i className="fas fa-info-circle"></i>
                    Images acceptées: JPG, PNG, WebP (max 5MB)
                  </p>
                </>
              )}
            </div>
          </div>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            style={{ display: 'none' }}
          />
          
          {/* Hidden: Manual path input removed - client should not see worker URLs */}
        </>
      )}
    </div>
  );
};

export default ImageUpload;




